import React, { useState, useEffect, useCallback } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Container, Row, Col, Table, Button, Form, Tabs, Tab, InputGroup } from 'react-bootstrap';
import { formatNumberWithCommas } from '../utilities';
import { notify, errorMessage } from '../notify';

// Paper consumption from the factory's day close: per day and per job, expected (theory) vs actual kg.
// Theory uses the paper the factory manager picked for each machine station, not the order entry paper.

const bdToday = () => new Date(Date.now() + 6 * 3600 * 1000).toISOString().slice(0, 10);
const addDays = (d, n) => {
    const t = new Date(`${d}T00:00:00Z`);
    t.setUTCDate(t.getUTCDate() + n);
    return t.toISOString().slice(0, 10);
};
const kg = (n) => (n == null ? '-' : formatNumberWithCommas(Math.round(Number(n) || 0)));
const pctBg = (p) => (p == null ? undefined : p < -2 ? '#f8d7da' : p <= 5 ? '#d1e7dd' : p <= 7 ? '#fff3cd' : '#f8d7da');
const cell = { padding: '3px 6px', verticalAlign: 'middle' };
const head = { ...cell, backgroundColor: '#d1e7dd', whiteSpace: 'nowrap' };

function PaperConsumption() {
    const [from, setFrom] = useState(addDays(bdToday(), -30));
    const [to, setTo] = useState(bdToday());
    const [job, setJob] = useState('');
    const [tab, setTab] = useState('jobs');
    const [days, setDays] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [open, setOpen] = useState({});
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [d, j] = await Promise.all([
                axios.get(`${window.backendUrl}/paper_consumption/days`, { params: { from, to } }),
                axios.get(`${window.backendUrl}/paper_consumption/jobs`, { params: { from, to, job: job.trim() } }),
            ]);
            setDays(d.data.days || []);
            setJobs(j.data.jobs || []);
        } catch (err) {
            notify.error(errorMessage(err, 'Could not load the report'));
        }
        setLoading(false);
    }, [from, to, job]);
    useEffect(() => { load(); }, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps

    const closedDays = days.filter((d) => d.Status === 'Closed');
    const tot = (list, k) => list.reduce((s, x) => s + (Number(x[k]) || 0), 0);
    const totalWaste = (list) => {
        const t = tot(list, 'theoryKg');
        return t > 0 ? Math.round(((tot(list, 'actualKg') - t - tot(list, 'trimKg')) / t) * 10000) / 100 : null;
    };

    return (
        <>
            <NavigationBar />
            <Container fluid>
                <Row className="justify-content-center mt-3">
                    <Col md={12} lg={11}>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <h4 className="mb-0 me-3">Paper Consumption</h4>
                            <Form.Control type="date" size="sm" style={{ width: 160 }} value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} />
                            <span>to</span>
                            <Form.Control type="date" size="sm" style={{ width: 160 }} value={to} onChange={(e) => e.target.value && setTo(e.target.value)} />
                            <InputGroup size="sm" style={{ width: 260 }}>
                                <Form.Control placeholder="Job no" value={job} onChange={(e) => setJob(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') load(); }} />
                                <Button onClick={load}>Show</Button>
                            </InputGroup>
                            {loading && <span className="text-muted small">Loading…</span>}
                        </div>
                        <div className="small text-muted mb-2">
                            Expected = made pcs × board m² × flute factor × GSM of the paper the factory picked. Trim = paper the roll is wider than the board.
                            Wastage = (actual − expected − trim) ÷ expected.
                        </div>
                        <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
                            <Tab eventKey="jobs" title="Per job">
                                <div style={{ overflowX: 'auto' }}>
                                    <Table bordered size="sm" style={{ fontSize: 13, minWidth: 1100 }}>
                                        <thead><tr>
                                            <th style={head}>Job</th><th style={head}>Client / Buyer</th><th style={head}>Item · Spec</th><th style={head}>Measurement</th>
                                            <th style={head}>Days</th><th style={head}>Made pcs</th><th style={head}>Expected kg</th><th style={head}>Trim kg</th>
                                            <th style={head}>Actual kg</th><th style={head}>Actual vs expected</th><th style={head}>Wastage</th><th style={head}>Paper cost Tk</th><th style={head}>Tk / pc</th>
                                        </tr></thead>
                                        <tbody>
                                            {jobs.map((l) => (
                                                <React.Fragment key={l.OrderGsmId}>
                                                    <tr style={{ cursor: 'pointer' }} onClick={() => setOpen((o) => ({ ...o, [l.OrderGsmId]: !o[l.OrderGsmId] }))}>
                                                        <td style={cell}>{open[l.OrderGsmId] ? '▾' : '▸'} {l.JobNumber}</td>
                                                        <td style={cell}>{l.PartyName}{l.BuyerName ? ` / ${l.BuyerName}` : ''}</td>
                                                        <td style={cell}>{l.ItemName} · {l.Specification}</td>
                                                        <td style={cell}>{[l.Length, l.Width, l.Height].filter((v) => Number(v)).join('x')} {l.Unit}</td>
                                                        <td style={cell}>{l.firstDate === l.lastDate ? l.firstDate : `${l.firstDate} – ${l.lastDate}`}</td>
                                                        <td style={cell}>{formatNumberWithCommas(l.madeQty)}</td>
                                                        <td style={cell}>{kg(l.theoryKg)}</td>
                                                        <td style={cell}>{kg(l.trimKg)}</td>
                                                        <td style={cell}><strong>{kg(l.actualKg)}</strong></td>
                                                        <td style={cell}>{l.diffPct == null ? '-' : `${l.diffPct > 0 ? '+' : ''}${l.diffPct}%`}</td>
                                                        <td style={{ ...cell, backgroundColor: pctBg(l.wastePct) }}>{l.wastePct == null ? '-' : `${l.wastePct}%`}</td>
                                                        <td style={cell}>{kg(l.costTk)}</td>
                                                        <td style={cell}>{l.madeQty > 0 ? (l.costTk / l.madeQty).toFixed(2) : '-'}</td>
                                                    </tr>
                                                    {open[l.OrderGsmId] && l.positions.map((p) => (
                                                        <tr key={`${l.OrderGsmId}|${p.Position}|${p.paper}`} style={{ backgroundColor: '#f8f9fa' }}>
                                                            <td style={cell}></td>
                                                            <td style={cell} colSpan={5}>{p.Position} — {p.paper || 'paper not picked'}</td>
                                                            <td style={cell}>{kg(p.TheoryKg)}</td>
                                                            <td style={cell}>{kg(p.TrimKg)}</td>
                                                            <td style={cell}>{kg(p.ActualKg)}</td>
                                                            <td style={cell} colSpan={2}></td>
                                                            <td style={cell}>{kg(p.CostTk)}</td>
                                                            <td style={cell}></td>
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                            {jobs.length > 0 && (
                                                <tr style={{ fontWeight: 'bold', backgroundColor: '#b7dfc4' }}>
                                                    <td style={cell} colSpan={5}>Total ({jobs.length} lines)</td>
                                                    <td style={cell}>{formatNumberWithCommas(tot(jobs, 'madeQty'))}</td>
                                                    <td style={cell}>{kg(tot(jobs, 'theoryKg'))}</td>
                                                    <td style={cell}>{kg(tot(jobs, 'trimKg'))}</td>
                                                    <td style={cell}>{kg(tot(jobs, 'actualKg'))}</td>
                                                    <td style={cell}></td>
                                                    <td style={{ ...cell, backgroundColor: pctBg(totalWaste(jobs)) }}>{totalWaste(jobs) == null ? '-' : `${totalWaste(jobs)}%`}</td>
                                                    <td style={cell}>{kg(tot(jobs, 'costTk'))}</td>
                                                    <td style={cell}></td>
                                                </tr>
                                            )}
                                            {!loading && !jobs.length && <tr><td colSpan={13} className="text-center text-muted" style={cell}>No closed days in this range</td></tr>}
                                        </tbody>
                                    </Table>
                                </div>
                            </Tab>
                            <Tab eventKey="days" title="Per day">
                                <Table bordered size="sm" style={{ fontSize: 13 }}>
                                    <thead><tr>
                                        <th style={head}>Date</th><th style={head}>Status</th><th style={head}>Jobs made / planned</th><th style={head}>Pcs made / planned</th>
                                        <th style={head}>Expected kg</th><th style={head}>Trim kg</th><th style={head}>Actual kg</th><th style={head}>Wastage</th>
                                        <th style={head}>Other use kg</th><th style={head}>Core pile kg</th><th style={head}>Paper cost Tk</th>
                                    </tr></thead>
                                    <tbody>
                                        {days.map((d) => (
                                            <tr key={d.PlanID}>
                                                <td style={cell}>{d.PlanDate}</td>
                                                <td style={cell}>{d.Status === 'Closed' ? 'Closed' : 'Open'}</td>
                                                <td style={cell}>{d.jobsMade} / {d.jobsPlanned}</td>
                                                <td style={cell}>{formatNumberWithCommas(d.pcsMade)} / {formatNumberWithCommas(d.pcsPlanned)}</td>
                                                <td style={cell}>{kg(d.theoryKg)}</td>
                                                <td style={cell}>{kg(d.trimKg)}</td>
                                                <td style={cell}><strong>{kg(d.actualKg)}</strong></td>
                                                <td style={{ ...cell, backgroundColor: pctBg(d.wastePct) }}>{d.wastePct == null ? '-' : `${d.wastePct}%`}</td>
                                                <td style={cell}>{kg(d.otherKg)}</td>
                                                <td style={cell}>{kg(d.coreKg)}</td>
                                                <td style={cell}>{kg(d.costTk)}</td>
                                            </tr>
                                        ))}
                                        {closedDays.length > 0 && (
                                            <tr style={{ fontWeight: 'bold', backgroundColor: '#b7dfc4' }}>
                                                <td style={cell} colSpan={4}>Total of {closedDays.length} closed day(s)</td>
                                                <td style={cell}>{kg(tot(closedDays, 'theoryKg'))}</td>
                                                <td style={cell}>{kg(tot(closedDays, 'trimKg'))}</td>
                                                <td style={cell}>{kg(tot(closedDays, 'actualKg'))}</td>
                                                <td style={{ ...cell, backgroundColor: pctBg(totalWaste(closedDays)) }}>{totalWaste(closedDays) == null ? '-' : `${totalWaste(closedDays)}%`}</td>
                                                <td style={cell}>{kg(tot(closedDays, 'otherKg'))}</td>
                                                <td style={cell}>{kg(tot(closedDays, 'coreKg'))}</td>
                                                <td style={cell}>{kg(tot(closedDays, 'costTk'))}</td>
                                            </tr>
                                        )}
                                        {!loading && !days.length && <tr><td colSpan={11} className="text-center text-muted" style={cell}>No days in this range</td></tr>}
                                    </tbody>
                                </Table>
                            </Tab>
                        </Tabs>
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default PaperConsumption;
